import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import useMobileView from '../../hooks/useMobileView';
import { mobileHideScrollbarSx } from '../layout/mobile/mobileAppTheme';
import PageBackNav from './PageBackNav';
import BookingSectionTabs from '../booking/BookingSectionTabs';
import FinanceSectionTabs from '../finance/FinanceSectionTabs';
import InventorySectionTabs from '../inventory/InventorySectionTabs';
import CustomerSectionTabs from '../customer/CustomerSectionTabs';
import './DashboardHeader.css';

const NAV_ACCENT = '#4ce1b6';

function isNavLinkActive(pathname, url) {
  if (!url) return false;
  if (pathname === url) return true;
  if (url.endsWith('/') && pathname === url.slice(0, -1)) return true;
  if (!url.endsWith('/') && `${pathname}/` === url) return true;
  return pathname.startsWith(`${url}/`);
}

function NavChip({ link, active, isDark, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        border: 'none',
        cursor: 'pointer',
        flexShrink: 0,
        borderRadius: '8px',
        px: 1.1,
        py: 0.55,
        fontSize: '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        color: active ? NAV_ACCENT : isDark ? '#94a3b8' : '#64748b',
        backgroundColor: active ? alpha(NAV_ACCENT, 0.12) : 'transparent',
        transition: 'color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          color: NAV_ACCENT,
          backgroundColor: alpha(NAV_ACCENT, isDark ? 0.1 : 0.08),
        },
      }}
    >
      {link.label}
    </Box>
  );
}

/**
 * Dashboard Header Component
 * Uses Material-UI components with theme-aware styling
 */
const DashboardHeader = ({
  title = 'Dashboard',
  navLinks = [],
  showDateFilter = false,
  showTimeFilter = false,
  showAdvancedLink = false,
  showNewButton = false,
  newButtonLabel = 'New',
  newButtonAction,
  datePickerId = 'default',
  breadcrumbs = null,
  showBreadcrumbs = true,
  showHomeIcon = true,
  showBackNav = true,
  backLabel = 'Back',
  backFallbackPath = '/dashboard/',
  forceDark = false,
  showBookingSectionTabs = false,
  showFinanceSectionTabs = false,
  showInventorySectionTabs = false,
  showCustomerSectionTabs = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompact } = useMobileView();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = forceDark || currentTheme === 'dark';
  const showSectionTabs = showBookingSectionTabs
    || showFinanceSectionTabs
    || showInventorySectionTabs
    || showCustomerSectionTabs;
  const SectionTabs = showBookingSectionTabs
    ? BookingSectionTabs
    : showFinanceSectionTabs
      ? FinanceSectionTabs
      : showInventorySectionTabs
        ? InventorySectionTabs
        : CustomerSectionTabs;
  // Breadcrumbs are replaced by history-back across dashboard pages.
  // Section-tab list pages also get back (they never used breadcrumbs).
  const showHistoryBack = showBackNav && (showBreadcrumbs || showSectionTabs);
  const headerTitle =
    title ||
    (Array.isArray(breadcrumbs) && breadcrumbs.length > 0
      ? breadcrumbs[breadcrumbs.length - 1]?.label
      : 'Dashboard');

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const headerBorderColor = isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08);

  const renderSecondaryNav = ({ desktopOnly = false, mobileScroll = false } = {}) => {
    if (navLinks.length === 0) return null;

    const useScroll = mobileScroll || (isCompact && !desktopOnly);

    return (
      <Box
        className={useScroll ? 'dashboard-nav-links-mobile' : 'dashboard-nav-links'}
        sx={{
          display: desktopOnly ? { xs: 'none', md: 'flex' } : 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'nowrap',
          ...(useScroll
            ? {
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                pb: 0.25,
                ...mobileHideScrollbarSx,
              }
            : {}),
        }}
        role="navigation"
        aria-label="Related pages"
      >
        {navLinks.map((link) => (
          <NavChip
            key={`${useScroll ? 'mobile-' : ''}${link.url}-${link.label}`}
            link={link}
            active={isNavLinkActive(location.pathname, link.url)}
            isDark={isDark}
            onClick={() => navigate(link.url)}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Box
        className="dashboard-nav-header"
        sx={{
          display: 'flex',
          alignItems: isCompact && showSectionTabs ? 'stretch' : 'center',
          justifyContent: 'space-between',
          flexDirection: isCompact && showSectionTabs ? 'column' : 'row',
          gap: isCompact && showSectionTabs ? 1 : { xs: 1, md: 1.5 },
          flexWrap: isCompact && showSectionTabs ? 'nowrap' : { xs: 'wrap', md: 'nowrap' },
          width: '100%',
          minWidth: 0,
          pb: 1.25,
          mb: isCompact ? 1 : 2,
          pt: isCompact ? 0 : 0.25,
          borderBottom: `1px solid ${headerBorderColor}`,
        }}
      >
        {showSectionTabs ? (
          isCompact ? (
            <>
              {showHistoryBack ? (
                <PageBackNav
                  label={backLabel}
                  fallbackPath={backFallbackPath}
                  isDark={isDark}
                  sx={{ mb: 0.25 }}
                />
              ) : null}
              <SectionTabs inline isDark={isDark} sx={{ width: '100%' }} />
              {renderSecondaryNav({ mobileScroll: true })}
            </>
          ) : (
            <>
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1.25,
                }}
              >
                {showHistoryBack ? (
                  <PageBackNav
                    label={backLabel}
                    fallbackPath={backFallbackPath}
                    isDark={isDark}
                  />
                ) : null}
                <SectionTabs inline isDark={isDark} />
              </Box>
              {renderSecondaryNav({ desktopOnly: true })}
            </>
          )
        ) : (
          <>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {showHistoryBack ? (
                <PageBackNav
                  label={backLabel}
                  fallbackPath={backFallbackPath}
                  isDark={isDark}
                  sx={{ mb: 0 }}
                />
              ) : (
                <Typography
                  variant="h4"
                  className="dashboard-nav-title"
                  sx={{
                    m: 0,
                    fontSize: isCompact ? '1.25rem' : '1.35rem',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: isDark ? '#f8fafc' : '#212121',
                  }}
                >
                  {headerTitle}
                </Typography>
              )}
            </Box>
            {navLinks.length > 0 ? renderSecondaryNav({ desktopOnly: true }) : null}
          </>
        )}
      </Box>

      {isCompact && navLinks.length > 0 && !showSectionTabs ? (
        <Box
          className="dashboard-nav-links-mobile"
          sx={{
            display: 'flex',
            gap: 0.5,
            overflowX: 'auto',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            WebkitOverflowScrolling: 'touch',
            pb: 0.25,
            mb: 1,
            ...mobileHideScrollbarSx,
          }}
          role="navigation"
          aria-label="Related pages"
        >
          {navLinks.map((link) => (
            <NavChip
              key={`mobile-${link.url}-${link.label}`}
              link={link}
              active={isNavLinkActive(location.pathname, link.url)}
              isDark={isDark}
              onClick={() => navigate(link.url)}
            />
          ))}
        </Box>
      ) : null}

      {(showDateFilter || showTimeFilter || showAdvancedLink || showNewButton) && (
        <Box
          className="dashboard-control-panel"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2.5,
            p: 2,
            flexWrap: 'wrap',
            backgroundColor: isDark ? '#2a2a31' : 'transparent',
            borderRadius: 1,
            transition: 'background-color 0.3s ease',
          }}
        >
          {showDateFilter && (
            <Box className="dashboard-date-range">
              {/* Date filter UI would go here */}
            </Box>
          )}
          {showAdvancedLink && (
            <Typography
              component="a"
              href="#"
              className="dashboard-advanced-link"
              sx={{
                color: '#4ce1b6',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  color: '#3dd1a3',
                  textDecoration: 'none',
                },
              }}
            >
              Advanced Options
            </Typography>
          )}
          {showNewButton && (
            <Box className="dashboard-new-btn" sx={{ ml: 'auto' }}>
              {/* New button would go here */}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default DashboardHeader;
